<script setup>
// Estúdio Social — Contas conectadas (Task 10). Lista do que já está
// conectado + contador de canais do plano. Conectar rede é em Canais →
// Adicionar Canal (decisão Ivan 08/10); esta tela segue sendo o retorno do
// OAuth (popup e passo de seleção headless). Direção visual do módulo (vale para o calendário/composer/
// métricas das Tasks 11-13): o badge de plataforma é o único lugar da tela
// com cor de marca saturada — o resto do chrome (cards, texto, botões, avisos)
// fica só nos tokens n-* pra não virar um dashboard "colorido" inteiro.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import socialAPI from 'dashboard/api/social';
import Button from 'dashboard/components-next/button/Button.vue';
import SocialNav from './SocialNav.vue';
import { SOCIAL_PLATFORMS } from './constants';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const { accountScopedRoute } = useAccount();
const { isAdmin } = useAdmin();

const isLoading = ref(true);
const loadError = ref(null); // { status, message } | null
const accounts = ref([]);
const limit = ref(0);
const connectedCount = ref(0);

const disconnectingId = ref(null);
const isSyncing = ref(false);

const hasEverConnected = computed(() => accounts.value.length > 0);
// Durante o carregamento inicial ainda não sabemos se a conta já conectou
// alguma rede antes — mostrar a cópia de "estado vazio" nesse momento pisca
// pra quem já tem redes conectadas (só descobrimos depois que a resposta
// chega). Título/subtítulo genéricos até isLoading resolver.
const headerTitle = computed(() =>
  !isLoading.value && !hasEverConnected.value
    ? t('SOCIAL_STUDIO.ACCOUNTS.EMPTY_TITLE')
    : t('SOCIAL_STUDIO.ACCOUNTS.TITLE')
);
const headerSubtitle = computed(() =>
  !isLoading.value && !hasEverConnected.value
    ? t('SOCIAL_STUDIO.ACCOUNTS.EMPTY_SUBTITLE')
    : t('SOCIAL_STUDIO.ACCOUNTS.SUBTITLE')
);
const limitReached = computed(
  () => limit.value > 0 && connectedCount.value >= limit.value
);

const fetchAccounts = async () => {
  isLoading.value = true;
  loadError.value = null;
  try {
    const { data } = await socialAPI.accounts();
    accounts.value = data.accounts || [];
    limit.value = data.limit ?? 0;
    connectedCount.value = data.connected_count ?? 0;
  } catch (error) {
    loadError.value = {
      status: error.response?.status,
      message:
        error.response?.data?.error ||
        error.response?.data?.errors?.[0] ||
        t('SOCIAL_STUDIO.ACCOUNTS.LOADING_ERROR'),
    };
  } finally {
    isLoading.value = false;
  }
};

// -- Conexão headless: a rede redireciona pra ESTA página (no popup) com
// step+tempToken; montamos a seleção de página com a nossa cara. ---------
const headless = ref(null); // { platform, step, tempToken, userProfile }
const headlessOptions = ref([]);
const headlessSelected = ref('');
const headlessState = ref('loading'); // loading | pick | done | expired | error

// O OAuth abre num popup com noopener (window.opener é null): o nome da
// janela é o que identifica o popup de conexão.
const isConnectPopup = () => window.name === 'chatsuite-social-connect';

// O backend (Social::HeadlessSelection) já devolve {id, name, detail} para
// qualquer rede — página, organização, quadro ou local.
const headlessParams = () => ({
  platform: headless.value.platform,
  step: headless.value.step,
  temp_token: headless.value.tempToken,
  pending_data_token: headless.value.pendingDataToken,
  connect_token: headless.value.connectToken,
  profile_id: headless.value.profileId,
});

async function startHeadlessSelection() {
  const query = route.query;
  const tempToken = query.tempToken || query.temp_token;
  // LinkedIn, Pinterest e Google voltam com pendingDataToken, sem tempToken:
  // antes a tela ignorava o passo e a conexão nunca terminava.
  const pendingDataToken = query.pendingDataToken;
  if (!query.step || (!tempToken && !pendingDataToken)) return;
  headless.value = {
    platform: query.platform,
    step: query.step,
    tempToken,
    pendingDataToken,
    connectToken: query.connect_token,
    userProfile: query.userProfile,
    // Multi-conta: o redirect diz em QUAL perfil Zernio o connect rodou.
    profileId: query.profileId,
  };
  router.replace({ query: {} });
  try {
    const { data } = await socialAPI.connectOptions(headlessParams());
    headlessOptions.value = data.options || [];
    if (headlessOptions.value.length === 1)
      headlessSelected.value = headlessOptions.value[0].id;
    headlessState.value = 'pick';
  } catch (error) {
    headlessState.value = error?.response?.status === 422 ? 'expired' : 'error';
  }
}

async function finalizeHeadless() {
  headlessState.value = 'loading';
  try {
    await socialAPI.connectFinalize({
      ...headlessParams(),
      user_profile: headless.value.userProfile,
      selection_id: headlessSelected.value,
    });
    headlessState.value = 'done';
    fetchAccounts();
    // No popup: a janela principal recarrega no focus (onWindowFocus). O
    // popup abre com noopener (window.opener é null): o nome da janela é o
    // que identifica.
    if (isConnectPopup() || window.opener) {
      setTimeout(() => window.close(), 1500);
    }
  } catch (error) {
    headlessState.value = error?.response?.status === 422 ? 'expired' : 'error';
  }
}

// O OAuth abre num POPUP centralizado (pedido do produto: não tirar o
// cliente da tela); quando o usuário volta pra
// esta aba, é o melhor sinal disponível de "talvez tenha terminado de
// conectar" — sem isso a lista fica presa no estado de antes da conexão até
// um refresh manual.
const onWindowFocus = () => {
  if (!isLoading.value) fetchAccounts();
};

// O OAuth devolve o popup para esta tela. Sem passo de seleção (Instagram,
// TikTok…), não há nada a escolher: sincroniza e fecha, e quem abriu (Canais)
// confere a conexão ao voltar o foco. Antes o popup ficava aberto mostrando
// esta tela inteira, com outra grade de conectar.
const popupClosing = ref(false);
const closePopupAfterOauth = async () => {
  if (!isConnectPopup() || route.query.step || route.query.error) return false;
  popupClosing.value = true;
  await socialAPI.syncAccounts().catch(() => {});
  setTimeout(() => window.close(), 800);
  return true;
};

onMounted(async () => {
  if (await closePopupAfterOauth()) return;
  fetchAccounts();
  startHeadlessSelection();
  window.addEventListener('focus', onWindowFocus);
});
onBeforeUnmount(() => {
  window.removeEventListener('focus', onWindowFocus);
});

// Reconciliação sob demanda: cobre o webhook mudo/atrasado (a conta conectou
// do lado da Zernio mas o evento não chegou ainda) sem precisar de refresh.
const syncAccounts = async () => {
  if (isSyncing.value) return;
  isSyncing.value = true;
  try {
    const { data } = await socialAPI.syncAccounts();
    accounts.value = data.accounts || [];
    limit.value = data.limit ?? 0;
    connectedCount.value = data.connected_count ?? 0;
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.response?.data?.errors?.[0] ||
        t('SOCIAL_STUDIO.ACCOUNTS.SYNC_ERROR')
    );
  } finally {
    isSyncing.value = false;
  }
};

const disconnect = async account => {
  const platformLabel = t(
    `SOCIAL_STUDIO.ACCOUNTS.PLATFORMS.${account.platform.toUpperCase()}`
  );
  const confirmMessage = t('SOCIAL_STUDIO.ACCOUNTS.CONFIRM_DISCONNECT', {
    username: account.username || platformLabel,
    platform: platformLabel,
  });
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!window.confirm(confirmMessage)) return;

  disconnectingId.value = account.id;
  try {
    await socialAPI.disconnect(account.id);
    useAlert(t('SOCIAL_STUDIO.ACCOUNTS.DISCONNECTED_SUCCESS'));
    await fetchAccounts();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.response?.data?.errors?.[0] ||
        t('SOCIAL_STUDIO.ACCOUNTS.GENERIC_ERROR')
    );
  } finally {
    disconnectingId.value = null;
  }
};

const platformMeta = key => SOCIAL_PLATFORMS.find(p => p.key === key);
const statusTone = status =>
  status === 'connected'
    ? 'border-n-teal-6 bg-n-teal-3 text-n-teal-11'
    : 'border-n-slate-6 bg-n-slate-3 text-n-slate-11';
</script>

<template>
  <div class="w-full h-full overflow-y-auto bg-n-background">
    <div class="max-w-6xl mx-auto p-6 space-y-8">
      <SocialNav />

      <!-- Cabeçalho: título muda no primeiro acesso (estado vazio é a própria
         grade de conexão, só com uma chamada mais calorosa em cima). -->
      <div
        class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
      >
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ headerTitle }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11 max-w-md">
            {{ headerSubtitle }}
          </p>
        </div>

        <div
          v-if="!isLoading && !loadError"
          class="flex shrink-0 items-center gap-2"
        >
          <div
            class="rounded-full border px-3 py-1.5 text-xs font-medium tabular-nums"
            :class="
              limitReached
                ? 'border-n-amber-6 bg-n-amber-2 text-n-amber-11'
                : 'border-n-weak text-n-slate-11'
            "
          >
            {{
              t('SOCIAL_STUDIO.ACCOUNTS.LIMIT_LABEL', {
                used: connectedCount,
                limit,
              })
            }}
          </div>
          <Button
            :label="
              isSyncing
                ? t('SOCIAL_STUDIO.ACCOUNTS.SYNCING')
                : t('SOCIAL_STUDIO.ACCOUNTS.SYNC')
            "
            variant="ghost"
            size="sm"
            icon="i-lucide-refresh-cw"
            :is-loading="isSyncing"
            :disabled="isSyncing"
            @click="syncAccounts"
          />
        </div>
      </div>

      <!-- Limite atingido: aviso calmo, não é um erro — é um fato de plano. -->
      <div
        v-if="!isLoading && !loadError && limitReached"
        class="flex flex-col gap-2 rounded-2xl border border-n-amber-6 bg-n-amber-2 p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <p class="text-sm text-n-amber-11">
          {{ t('SOCIAL_STUDIO.ACCOUNTS.LIMIT_REACHED') }}
        </p>
        <RouterLink
          :to="accountScopedRoute('billing_settings_index')"
          class="shrink-0 text-sm font-semibold text-n-amber-11 underline underline-offset-2 hover:opacity-80"
        >
          {{ t('SOCIAL_STUDIO.ACCOUNTS.UPGRADE_CTA') }}
        </RouterLink>
      </div>

      <!-- Erro ao carregar (inclui 402: módulo não ativo) -->
      <div
        v-if="!isLoading && loadError"
        class="rounded-2xl border border-n-weak bg-n-solid-2 p-6 text-center space-y-3"
      >
        <span
          class="i-lucide-cloud-alert mx-auto block size-8 text-n-slate-10"
        />
        <p class="text-sm text-n-slate-11">{{ loadError.message }}</p>
        <Button
          v-if="loadError.status === 402"
          :label="t('SOCIAL_STUDIO.ACCOUNTS.UPGRADE_CTA')"
          size="sm"
          @click="router.push(accountScopedRoute('billing_settings_index'))"
        />
        <Button
          v-else
          :label="t('SOCIAL_STUDIO.ACCOUNTS.RETRY')"
          variant="outline"
          size="sm"
          @click="fetchAccounts"
        />
      </div>

      <!-- Loading: esqueleto no formato do grid (mesma grade de Task 12/13),
         em vez de um spinner solto no meio da tela. -->
      <div
        v-else-if="isLoading"
        class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
      >
        <div
          v-for="n in 8"
          :key="n"
          class="h-[124px] animate-pulse rounded-2xl border border-n-weak bg-n-slate-3"
        />
      </div>

      <template v-else>
        <!-- Conectadas -->
        <section v-if="hasEverConnected" class="space-y-3">
          <h2
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('SOCIAL_STUDIO.ACCOUNTS.CONNECTED_TITLE') }}
          </h2>
          <div class="space-y-2">
            <div
              v-for="account in accounts"
              :key="account.id"
              class="flex items-center justify-between gap-3 rounded-2xl border border-n-weak bg-n-solid-2 p-3"
            >
              <div class="flex min-w-0 items-center gap-3">
                <span
                  class="flex size-9 shrink-0 items-center justify-center rounded-xl"
                  :class="[
                    platformMeta(account.platform)?.badgeClass,
                    platformMeta(account.platform)?.iconTone || 'text-white',
                  ]"
                >
                  <span
                    :class="platformMeta(account.platform)?.icon"
                    class="size-4"
                  />
                </span>
                <div class="min-w-0">
                  <p class="truncate text-sm font-medium text-n-slate-12">
                    {{
                      account.username ||
                      t(
                        `SOCIAL_STUDIO.ACCOUNTS.PLATFORMS.${account.platform.toUpperCase()}`
                      )
                    }}
                  </p>
                  <p class="text-xs text-n-slate-10">
                    {{
                      t(
                        `SOCIAL_STUDIO.ACCOUNTS.PLATFORMS.${account.platform.toUpperCase()}`
                      )
                    }}
                  </p>
                </div>
              </div>
              <div class="flex shrink-0 items-center gap-2">
                <span
                  class="rounded-full border px-2 py-0.5 text-[11px] font-medium"
                  :class="statusTone(account.status)"
                >
                  {{
                    account.status === 'connected'
                      ? t('SOCIAL_STUDIO.ACCOUNTS.CONNECTED')
                      : t('SOCIAL_STUDIO.ACCOUNTS.DISCONNECTED')
                  }}
                </span>
                <Button
                  v-if="account.status === 'connected' && isAdmin"
                  :label="
                    disconnectingId === account.id
                      ? t('SOCIAL_STUDIO.ACCOUNTS.DISCONNECTING')
                      : t('SOCIAL_STUDIO.ACCOUNTS.DISCONNECT')
                  "
                  variant="outline"
                  color="ruby"
                  size="sm"
                  :is-loading="disconnectingId === account.id"
                  @click="disconnect(account)"
                />
              </div>
            </div>
          </div>
        </section>

        <!-- Conectar rede é em Canais (decisão Ivan 08/10): cada conta
           conectada ocupa 1 canal do plano. O Estúdio só publica e mede. -->
        <section
          v-if="isAdmin"
          class="flex flex-col gap-3 rounded-2xl border border-n-weak bg-n-solid-2 p-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="min-w-0">
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('SOCIAL_STUDIO.ACCOUNTS.AVAILABLE_TITLE') }}
            </h2>
            <p class="mt-1 text-xs text-n-slate-10">
              {{
                limitReached
                  ? t('SOCIAL_STUDIO.ACCOUNTS.LIMIT_REACHED_SHORT')
                  : t('SOCIAL_STUDIO.ACCOUNTS.CONNECT_IN_CHANNELS')
              }}
            </p>
          </div>
          <Button
            :label="t('SOCIAL_STUDIO.ACCOUNTS.GO_TO_CHANNELS')"
            icon="i-lucide-circle-plus"
            size="sm"
            class="shrink-0"
            @click="router.push(accountScopedRoute('settings_inbox_new'))"
          />
        </section>

        <!-- Agente não-admin: nada pra conectar, só o aviso -->
        <p v-else-if="!hasEverConnected" class="text-sm text-n-slate-10">
          {{ t('SOCIAL_STUDIO.ACCOUNTS.NON_ADMIN_EMPTY') }}
        </p>
      </template>
    </div>
  </div>

  <!-- Popup voltando do OAuth sem seleção: só avisa e fecha -->
  <div
    v-if="popupClosing"
    class="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-n-background p-4 text-center"
  >
    <span class="i-lucide-circle-check size-8 text-n-teal-9" />
    <p class="text-sm text-n-slate-12">
      {{ t('SOCIAL_STUDIO.ACCOUNTS.POPUP_DONE') }}
    </p>
  </div>

  <!-- Seleção headless (white-label): monta no lugar da página da Zernio -->
  <div
    v-if="headless"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
  >
    <div
      class="w-full max-w-md rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl p-6 space-y-4"
    >
      <p class="text-base font-semibold text-n-slate-12">
        {{ t('SOCIAL_STUDIO.ACCOUNTS.HEADLESS.TITLE') }}
      </p>

      <div v-if="headlessState === 'loading'" class="flex justify-center py-6">
        <span
          class="i-lucide-loader-circle w-6 h-6 animate-spin text-n-slate-9"
        />
      </div>

      <template v-else-if="headlessState === 'pick'">
        <p class="text-sm text-n-slate-10">
          {{ t('SOCIAL_STUDIO.ACCOUNTS.HEADLESS.SUBTITLE') }}
        </p>
        <div class="space-y-2 max-h-64 overflow-y-auto">
          <label
            v-for="option in headlessOptions"
            :key="option.id"
            class="flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors"
            :class="
              headlessSelected === option.id
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak hover:bg-n-alpha-1'
            "
          >
            <input
              v-model="headlessSelected"
              type="radio"
              :value="option.id"
              class="accent-current"
            />
            <span class="flex flex-col min-w-0">
              <span class="text-sm text-n-slate-12">{{ option.name }}</span>
              <span v-if="option.detail" class="text-xs text-n-slate-10">
                {{ option.detail }}
              </span>
            </span>
          </label>
        </div>
        <button
          class="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-xl bg-n-brand hover:bg-n-brand-dark transition-colors disabled:opacity-50"
          :disabled="!headlessSelected"
          @click="finalizeHeadless"
        >
          <span class="i-lucide-check w-4 h-4" />
          {{ t('SOCIAL_STUDIO.ACCOUNTS.HEADLESS.CONFIRM') }}
        </button>
      </template>

      <p
        v-else-if="headlessState === 'done'"
        class="text-sm text-green-600 dark:text-green-400 flex items-center gap-2"
      >
        <span class="i-lucide-circle-check w-4 h-4" />
        {{ t('SOCIAL_STUDIO.ACCOUNTS.HEADLESS.SUCCESS') }}
      </p>
      <p v-else class="text-sm text-n-slate-11">
        {{
          headlessState === 'expired'
            ? t('SOCIAL_STUDIO.ACCOUNTS.HEADLESS.EXPIRED')
            : t('SOCIAL_STUDIO.ACCOUNTS.HEADLESS.ERROR')
        }}
      </p>
    </div>
  </div>
</template>
